import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MatTableDataSource } from '@angular/material/table';
import { HiararchyService } from 'src/app/apis/hiararchy.service';
import { SubjectService } from 'src/app/apis/subject.service';
import { TeacherService } from 'src/app/apis/teacher.service';
import { Sector } from 'src/app/viewModels/sector';

declare var $: any;

type ReportTeacherLine = {
  teacher_id: number;
  teacher_name: string;
  items: Array<{
    subject_type: string;
    nb_hours: number;
    week_type_id?: number;
    groups: Array<{ id: number; abbreviation: string }>;
  }>;
};

type ReportSubjectLine = {
  subject_id: number;
  subject_code?: string;
  abbreviation?: string;
  designation?: string;
  year_division_id?: number;
  teachers: ReportTeacherLine[];
};

type ReportRow = {
  subject: string;
  teacher: string;
  subject_type: string;
  nb_hours: number;
  groups: string;
};

@Component({
  selector: 'app-subjects-teachers',
  templateUrl: './subjects-teachers.component.html',
  styleUrls: ['./subjects-teachers.component.css']
})
export class SubjectsTeachersComponent implements OnInit {
  sectors: Sector[] = [];
  selectedSector: number;

  reportSem1: ReportSubjectLine[] = [];
  reportSem2: ReportSubjectLine[] = [];
  reportOther: ReportSubjectLine[] = [];
  isLoading = false;

  displayedColumns: string[] = ['subject', 'teacher', 'subject_type', 'nb_hours', 'groups'];
  dataSourceSem1 = new MatTableDataSource<ReportRow>([]);
  dataSourceSem2 = new MatTableDataSource<ReportRow>([]);
  dataSourceOther = new MatTableDataSource<ReportRow>([]);

  private sectionsById: Record<number, any> = {};
  private groupTdById: Record<number, any> = {};
  private groupTpById: Record<number, any> = {};
  private teacherById: Record<number, any> = {};

  constructor(
    public translate: TranslateService,
    private toastr: ToastrService,
    private hierarchy: HiararchyService,
    private subjectApis: SubjectService,
    private teacherApis: TeacherService
  ) {}

  ngOnInit(): void {
    this.hierarchy.getHiararchySectors().subscribe((data: Sector[]) => {
      this.sectors = data || [];
      if (this.sectors.length > 0) {
        this.selectedSector = this.sectors[0].id;
        this.load();
      }
    });
  }

  changeSector() {
    this.load();
  }

  load() {
    if (!this.selectedSector) return;
    this.isLoading = true;
    this.reportSem1 = [];
    this.reportSem2 = [];
    this.reportOther = [];
    this.dataSourceSem1.data = [];
    this.dataSourceSem2.data = [];
    this.dataSourceOther.data = [];

    this.hierarchy.getSectorSections(this.selectedSector).subscribe((sections: any[]) => {
      this.sectionsById = {};
      (sections || []).forEach((s) => (this.sectionsById[Number(s.id)] = s));

      this.hierarchy.getSectorGroupTds(this.selectedSector).subscribe((gtd: any[]) => {
        this.groupTdById = {};
        (gtd || []).forEach((g) => (this.groupTdById[Number(g.id)] = g));

        this.hierarchy.getSectorGroupTps(this.selectedSector).subscribe((gtp: any[]) => {
          this.groupTpById = {};
          (gtp || []).forEach((g) => (this.groupTpById[Number(g.id)] = g));

          this.teacherApis.getEstablishmentTeachers().subscribe((teachers: any[]) => {
            this.teacherById = {};
            (teachers || []).forEach((t) => (this.teacherById[Number(t.id)] = t));

            this.subjectApis.getSectorSubjects(this.selectedSector).subscribe((subjects: any[]) => {
              const all = this.buildReport(subjects || []);
              this.reportSem1 = all.filter((x) => Number(x.year_division_id) === 1);
              this.reportSem2 = all.filter((x) => Number(x.year_division_id) === 2);
              this.reportOther = all.filter((x) => Number(x.year_division_id) !== 1 && Number(x.year_division_id) !== 2);
              this.dataSourceSem1.data = this.flatten(this.reportSem1);
              this.dataSourceSem2.data = this.flatten(this.reportSem2);
              this.dataSourceOther.data = this.flatten(this.reportOther);
              this.isLoading = false;
            }, () => {
              this.isLoading = false;
              this.toastr.error(this.translate.instant('server_error'));
            });
          }, () => {
            this.isLoading = false;
            this.toastr.error(this.translate.instant('server_error'));
          });
        }, () => {
          this.isLoading = false;
          this.toastr.error(this.translate.instant('server_error'));
        });
      }, () => {
        this.isLoading = false;
        this.toastr.error(this.translate.instant('server_error'));
      });
    }, () => {
      this.isLoading = false;
      this.toastr.error(this.translate.instant('server_error'));
    });
  }

  private buildReport(subjects: any[]): ReportSubjectLine[] {
    const out: ReportSubjectLine[] = [];

    subjects.forEach((subj) => {
      const perTeacher: Record<number, ReportTeacherLine> = {};

      (subj.regimes || []).forEach((st: any) => {
        const pivot = st?.pivot || {};
        const teacher_id = Number(pivot.teacher_id || 0);
        if (!teacher_id) return;

        if (!perTeacher[teacher_id]) {
          const t = this.teacherById[teacher_id];
          perTeacher[teacher_id] = {
            teacher_id,
            teacher_name: t ? `${t.first_name || ''} ${t.last_name || ''}`.trim() : `#${teacher_id}`,
            items: []
          };
        }

        const groupIds = this.safeParseGroupIds(pivot.group_id);
        const groups = this.resolveGroups(st?.abbreviation, groupIds);

        perTeacher[teacher_id].items.push({
          subject_type: st?.abbreviation,
          nb_hours: Number(pivot.nb_hours || 0),
          week_type_id: pivot.week_type_id ? Number(pivot.week_type_id) : undefined,
          groups
        });
      });

      const teachers = Object.values(perTeacher).sort((a, b) => a.teacher_name.localeCompare(b.teacher_name));
      if (teachers.length === 0) return;

      out.push({
        subject_id: Number(subj.id),
        subject_code: subj.subject_code,
        abbreviation: subj.abbreviation,
        designation: subj.designation,
        year_division_id: subj.year_division_id ? Number(subj.year_division_id) : undefined,
        teachers
      });
    });

    return out.sort((a, b) => (a.abbreviation || '').localeCompare(b.abbreviation || ''));
  }

  private safeParseGroupIds(group_id: any): number[] {
    if (!group_id) return [];
    if (Array.isArray(group_id)) return group_id.map((x) => Number(x)).filter((x) => !!x);
    if (typeof group_id === 'number') return [group_id];
    if (typeof group_id === 'string') {
      try {
        const parsed = JSON.parse(group_id);
        if (Array.isArray(parsed)) return parsed.map((x) => Number(x)).filter((x) => !!x);
        if (parsed) return [Number(parsed)].filter((x) => !!x);
      } catch {
        // ignore
      }
    }
    return [];
  }

  private resolveGroups(subjectTypeAbbrev: string, ids: number[]): Array<{ id: number; abbreviation: string }> {
    const res: Array<{ id: number; abbreviation: string }> = [];
    ids.forEach((id) => {
      let obj: any;
      if (subjectTypeAbbrev === 'C') obj = this.sectionsById[id];
      else if (subjectTypeAbbrev === 'TD') obj = this.groupTdById[id];
      else if (subjectTypeAbbrev === 'TP') obj = this.groupTpById[id];
      res.push({ id, abbreviation: obj?.abbreviation || String(id) });
    });
    return res;
  }

  private flatten(list: ReportSubjectLine[]): ReportRow[] {
    const rows: ReportRow[] = [];
    list.forEach((subj) => {
      const subjLabel = `${subj.abbreviation || ''} - ${subj.designation || ''}${subj.subject_code ? ' (' + subj.subject_code + ')' : ''}`.trim();
      subj.teachers.forEach((t) => {
        t.items.forEach((it) => {
          rows.push({
            subject: subjLabel,
            teacher: t.teacher_name,
            subject_type: it.subject_type,
            nb_hours: it.nb_hours,
            groups: (it.groups || []).map((g) => g.abbreviation).join(', ')
          });
        });
      });
    });
    return rows;
  }

  print() {
    const $print = $('#print_subject_teachers')
      .clone()
      .addClass('ums_print')
      .prependTo('body');
    window.print();
    $print.remove();
  }
}

